import React from 'react';
import { Box, Button, Stack, useMediaQuery, useTheme, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarFilterButton,
	useGridApiContext,
} from '@mui/x-data-grid-pro';
import MoreActionButton from './MoreActionButton';
import { push, Routes } from 'routing';
import { exportInventory } from '../inventoryExport';
import { useDispatch } from 'react-redux';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization';

interface Props {
	showImport: boolean;
}

const TableToolbar = ({ showImport = false }: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const classes = useStyles();
	const apiRef = useGridApiContext();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleImport = () => {
		push(Routes.INVENTORY_IMPORT);
	};

	const handleExport = () => {
		const dateNow = new Date();
		const yyyymmddDate = dateNow.toISOString().split('T')[0];
		try {
			const visibleColumnFieldsWithExport = apiRef.current
				.getVisibleColumns()
				.filter((c) => !c.disableExport)
				.map((c) => c.field);
			const idField = 'id';
			const exportColumns = [idField, ...visibleColumnFieldsWithExport];
			exportInventory(apiRef, {
				utf8WithBom: true,
				fileName: `rentle_${yyyymmddDate}`,
				delimiter: ';',
				fields: exportColumns,
			});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					variant: 'error',
					message: t('common:errors.error'),
				}),
			);
		}
	};

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			flexWrap="wrap"
			className={classes.root}
		>
			<GridToolbarContainer>
				<Stack direction="row" spacing={1}>
					<GridToolbarColumnsButton sx={{ minWidth: 0 }} />
					<GridToolbarFilterButton sx={{ minWidth: 0 }} />
				</Stack>
			</GridToolbarContainer>
			<GridToolbarContainer>
				{!isMobile ? (
					<Stack direction="row" spacing={1}>
						<Button variant="text" color="secondary" onClick={handleExport} sx={{ minWidth: 0 }}>
							{t('dataGrid.toolbarExport', 'Export')}
						</Button>
						{showImport && (
							<Button variant="text" color="secondary" onClick={handleImport} sx={{ minWidth: 0 }}>
								{t('inventory.moreActionImport', 'Import')}
							</Button>
						)}
					</Stack>
				) : (
					<MoreActionButton triggerImport={handleImport} triggerExport={handleExport} />
				)}
			</GridToolbarContainer>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			'& .MuiDataGrid-toolbarContainer': {
				padding: theme.spacing(1),
			},
		},
	}),
);

export default TableToolbar;
